import React, { useState, useEffect, useRef } from "react";
import {
  getEmployeesByRole,
  startConversation,
} from "../../services/AdminPortal/chatAPI";
import { communicationAPI } from "../../services/communicationAPI";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";
import { XIcon } from "@heroicons/react/solid";

const EMPLOYEE_TYPES = [
  { label: "Staff", value: "staff" },
  { label: "Credit Manager", value: "credit-manager" },
  { label: "Banking Partner", value: "banking-partner" },
  { label: "Affiliate Partner", value: "affiliate-partner" },
  { label: "Admin", value: "admin" },
  { label: "Sub Admin", value: "subAdmin" },
];

const NewChatModal = ({ onClose, setActiveConversation, createConversation }) => {
  const [employeeType, setEmployeeType] = useState("");
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const { user } = useAuth();

  // Handle clicking outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch employees when type changes
  const handleTypeChange = async (e) => {
    const type = e.target.value;
    setEmployeeType(type);
    setSelected(null);
    setSearchTerm("");
    
    if (!type) {
      setEmployees([]);
      return;
    }
    
    setFetching(true);
    try {
      const emps = await getEmployeesByRole(type);
      setEmployees(Array.isArray(emps) ? emps : []);
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployees([]);
      toast.error("Failed to fetch employees");
    } finally {
      setFetching(false);
    }
  };

  // Start conversation using both APIs
  const handleStart = async () => {
    if (!selected) return;
    
    setLoading(true);
    try {
      
      // Use the passed createConversation function
      let conversation = null;
      try {
        
        // Format participants
        const participants = [
          {
            userId: selected._id,
            userType: employeeType,
            isActive: true
          }
        ];
        
        const response = await createConversation(
          participants,
          "direct",
          selected.name,
          `Direct message between ${user?.name || 'User'} and ${selected.name}`
        );
        
        if (response && response.conversation) {
          conversation = response.conversation;
        }
      } catch (commError) {
        console.error("Create conversation error:", commError);
      }
      
      // If communication API failed, try chat API
      if (!conversation) {
        try {
          const response = await startConversation(selected._id, employeeType);
          conversation = response;
        } catch (chatError) {
          console.error("Chat API error:", chatError);
          throw new Error("Failed to create conversation");
        }
      }
      
      // Enhance with participant details
      const enhancedConv = {
        ...conversation,
        _id: conversation._id || conversation.conversationId,
        conversationId: conversation.conversationId || conversation._id,
        participant: selected,
        receiverType: employeeType,
        receiverId: selected._id
      };
      
      // Store in localStorage for persistence
      try {
        // Remove any existing conversations with this participant
        let existingConvs = JSON.parse(localStorage.getItem('conversations') || '[]');
        existingConvs = existingConvs.filter(c => 
          c.participant?._id !== selected._id && 
          c.receiverId !== selected._id
        );
        
        // Add new conversation
        existingConvs.push({
          _id: enhancedConv._id || enhancedConv.conversationId,
          conversationId: enhancedConv._id || enhancedConv.conversationId,
          participant: {
            _id: selected._id,
            name: selected.name,
            userType: employeeType
          },
          receiverId: selected._id,
          receiverType: employeeType,
          lastMessageSnippet: 'New conversation started',
          updatedAt: new Date()
        });
        
        localStorage.setItem('conversations', JSON.stringify(existingConvs));
      } catch (e) {
        console.error('Error storing conversation in localStorage:', e);
      }
      
      // Set as active conversation
      setActiveConversation(enhancedConv);
      toast.success(`Chat started with ${selected.name}`);
      onClose();
    } catch (err) {
      console.error("Error starting conversation:", err);
      toast.error("Failed to start conversation");
    } finally {
      setLoading(false);
    }
  };

  // Filter employees based on search term
  const filteredEmployees = employees.filter((emp) =>
    emp.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[#222] p-6 rounded-lg w-96">
        <div className="flex items-center justify-between mb-4">
          <div className="text-lg font-semibold text-white">
            Start New Chat
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Employee Type Selection */}
        <select
          className="w-full p-2 rounded mb-4 bg-[#181818] text-white border border-gray-600"
          value={employeeType}
          onChange={handleTypeChange}
        >
          <option value="">Select Employee Type</option>
          {EMPLOYEE_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>

        {/* Employee Selection with Search */}
        {employeeType && (
          <div className="relative mb-4" ref={dropdownRef}>
            <input
              type="text"
              placeholder={fetching ? "Loading..." : "Search employees..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setShowDropdown(true)}
              className="w-full p-2 rounded bg-[#181818] text-white border border-gray-600"
              disabled={fetching}
            />

            {showDropdown && !fetching && (
              <div className="absolute z-10 w-full mt-1 bg-[#181818] border border-gray-600 rounded max-h-48 overflow-y-auto">
                {filteredEmployees.length === 0 ? (
                  <div className="p-2 text-gray-400 text-sm">
                    {employees.length === 0 ? (
                      <div>
                        <div>No employees found for this type.</div>
                        <div className="text-xs mt-1">
                          Available types: staff, credit-manager,
                          banking-partner, affiliate-partner, admin, subAdmin
                        </div>
                      </div>
                    ) : (
                      "No employees match your search."
                    )}
                  </div>
                ) : (
                  filteredEmployees.map((emp) => (
                    <div
                      key={emp._id}
                      className={`p-2 cursor-pointer hover:bg-[#333] text-white ${
                        selected?._id === emp._id
                          ? "bg-[#FFD600] text-black"
                          : ""
                      }`}
                      onClick={() => {
                        setSelected(emp);
                        setSearchTerm(emp.name);
                        setShowDropdown(false);
                      }}
                    >
                      {emp.name}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* Selected Employee Display */}
        {selected && (
          <div className="mb-4 p-2 bg-[#181818] rounded border border-gray-600">
            <div className="text-sm text-gray-400">Selected:</div>
            <div className="text-white font-medium">{selected.name}</div>
          </div>
        )}

        {/* Error Message */}
        {employeeType && !fetching && employees.length === 0 && (
          <div className="text-red-400 text-sm mb-2">
            No employees found for this type.
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            className="px-4 py-1 rounded bg-gray-500 text-white"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-1 rounded bg-[#FFD600] text-black font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleStart}
            disabled={!selected || loading}
          >
            {loading ? "Starting..." : "Start Chat"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewChatModal;